import { FC, useMemo, useState } from 'react';
import moment, { Moment } from 'moment-timezone';
import { getTimezone, sleep } from '@mgm/common/utils';
import { useTranslation } from 'react-i18next';
import { TBaseTable } from '@mgm/common/components/table';
import { CustomToolbar } from '../../../../components/table';
import { useGetScheduleDataAPI } from '../../../../hooks/api/useSchedules';
import { useRangePicker } from '../../../../hooks/useCommon';

const TimeoffTableHeader: FC<TBaseTable> = ({
  apiRef,
  printRef,
  handleScrollTable,
  isFullTable = false,
  setIsFullTable = () => {},
  handleExport,
}) => {
  const { t } = useTranslation(['common', 'dashboard', 'schedule']);
  const [isOpenCustomPicker, setIsOpenCustomPicker] = useState(false);

  const [menuOption, setMenuOption] = useState(t('thisMonth', { ns: 'dashboard' }));
  const [menuValue, setMenuValue] = useState('thisMonth');

  const {
    chosenDates, handleSetDates,
  } = useRangePicker();

  const { refetch: refetchShifts } = useGetScheduleDataAPI({
    dates: chosenDates, isMainSchedulesPage: true, type: 'timeoff',
  });

  const getCalendarShifts = async () => {
    await sleep(1000);
    await refetchShifts();
  };

  const items = useMemo(() => {
    const timezone = getTimezone();

    const dateRangePresets = {
      yesterday: [
        moment.tz(timezone).subtract(1, 'day').startOf('day'),
        moment.tz(timezone).subtract(1, 'day').endOf('day'),
      ],
      today: [
        moment.tz(timezone).startOf('day'),
        moment.tz(timezone).endOf('day'),
      ],
      last7Days: [
        moment.tz(timezone).subtract(6, 'days').startOf('day'),
        moment.tz(timezone).endOf('day'),
      ],
      last14Days: [
        moment.tz(timezone).subtract(13, 'days').startOf('day'),
        moment.tz(timezone).endOf('day'),
      ],
      last30Days: [
        moment.tz(timezone).subtract(29, 'days').startOf('day'),
        moment.tz(timezone).endOf('day'),
      ],
      thisMonth: [
        moment.tz(timezone).startOf('month'),
        moment.tz(timezone).endOf('month'),
      ],
    };

    const createItem = (name: string, value: string) => ({
      name,
      value,
      isSelected: menuValue === value,
      action: async () => {
        setMenuOption(name);
        setMenuValue(value);

        if (value !== 'custom') {
          handleSetDates(dateRangePresets[value as keyof typeof dateRangePresets] as [Moment, Moment]);
          await getCalendarShifts();
        }
      },
    });

    return [
      createItem(t('yesterday', { ns: 'dashboard' }), 'yesterday'),
      createItem(t('today', { ns: 'schedule' }), 'today'),
      createItem(t('lastSevenDays', { ns: 'dashboard' }), 'last7Days'),
      createItem(t('last14Days', { ns: 'dashboard' }), 'last14Days'),
      createItem(t('last30Days', { ns: 'dashboard' }), 'last30Days'),
      createItem(t('thisMonth', { ns: 'dashboard' }), 'thisMonth'),
      {
        name: t('customFixedDateRange', { ns: 'dashboard' }),
        value: 'custom',
        isSelected: menuValue === 'custom',
        action: () => {
          setMenuValue('custom');
          setIsOpenCustomPicker(true);
        },
      },
    ];
  }, [menuValue]);

  return (
    <CustomToolbar
      handleScrollTable={handleScrollTable}
      printRef={printRef}
      isDisplayCustomFilter={false}
      apiRef={apiRef}
      setFullTable={setIsFullTable}
      isFullTable={isFullTable}
      handleExport={handleExport}
      handleSetDates={handleSetDates}
      isDisplayPicker
      selectedPickerName={menuOption}
      pickerItems={items}
      isOpenCustomPicker={isOpenCustomPicker}
      onPickerSubmit={getCalendarShifts}
      setIsOpenCustomPicker={setIsOpenCustomPicker}
    />
  );
};

export {
  TimeoffTableHeader,
};
